import { InvoiceOverview } from '@vy/api-definition';
import { isInvoiceSyncToIntegrationAllowed } from '@vy/invoices';
import { INVOICE_REVIEW_STATUSES, InvoicePdfType } from '@vy/types';
import { saveUsageEventsReport } from '../utils/save-usage-events-report';
import { useCallback } from 'react';
import { useQueryClient } from 'react-query';
import { useGetInvoiceUsageEventsReport } from '../../../hooks/requests/use-get-invoice-usage-events-report';
import { useNavigate } from 'react-router-dom';
import {
  useExportUsageToMarketplace,
  useMorningExportInvoices,
  usePaddleExportInvoices,
  useQuickBooksExportInvoices,
  useSyncNetSuiteInvoice,
  useSyncNetSuiteInvoiceSalesOrder,
  useXeroExportInvoices,
} from '../../../hooks/requests/integrations';
import { useRefundInvoice } from '../../../hooks/requests/use-refund-invoice';
import { useSyncToStripe } from '../../../hooks/requests/use-sync-stripe';
import { useUpdateInvoicesReviewStatuses } from '../../../hooks/requests/use-update-invoices-review-statuses';
import { useDialog } from '../../../hooks/use-dialog';
import { useGetInvoicePdf } from '../../../hooks/use-get-invoice-pdf';
import { saveInvoiceAsFile } from '../../../services/invoices-file-saver';
import { queryKeys } from '../../../services/query-key/query-keys';
import { invalidateInvoicesQueries, invalidatePaymentsQueries } from '../../../utils/query-invalidation';
import { runMutation } from '../../../utils/vy-mutation';

export const useInvoiceActions = (invoice: InvoiceOverview) => {
  const downloadInvoicePdfMutation = useGetInvoicePdf();
  const syncToNetSuiteMutation = useSyncNetSuiteInvoice();
  const syncToNetSuiteSalesOrderMutation = useSyncNetSuiteInvoiceSalesOrder();
  const syncInvoicesToStripeMutation = useSyncToStripe();
  const refundInvoiceMutation = useRefundInvoice();
  const updateInvoicesReviewStatusesMutation = useUpdateInvoicesReviewStatuses();
  const exportInvoiceToQuickBooksMutation = useQuickBooksExportInvoices();
  const exportInvoiceToMorningMutation = useMorningExportInvoices();
  const exportInvoiceToXeroMutation = useXeroExportInvoices();
  const exportInvoiceToPaddleMutation = usePaddleExportInvoices();
  const exportUsageToMarketplaceMutation = useExportUsageToMarketplace();
  const downloadUsageEventsReportMutation = useGetInvoiceUsageEventsReport();

  const queryClient = useQueryClient();

  const navigate = useNavigate();

  const {
    isDialogOpen: isSalesOrderConfirmDialogOpen,
    handleClose: handleCloseSalesOrderConfirmDialog,
    handleOpen: handleOpenSalesOrderConfirmDialog,
  } = useDialog();

  const isInvoiceApplicableForSync = isInvoiceSyncToIntegrationAllowed(invoice);

  const downloadInvoicePdf = useCallback(
    async (pdfType: InvoicePdfType) => {
      await runMutation(
        downloadInvoicePdfMutation,
        {
          invoiceId: invoice.id,
          type: pdfType,
        },
        {
          pending: 'Downloading invoice...',
          success: 'Invoice downloaded',
          error: 'Failed to download invoice',
        },
        {
          onSuccess: async ({ encodedPdf }) =>
            saveInvoiceAsFile(encodedPdf, invoice.customer.name, invoice.name),
        },
      );
    },
    [syncToNetSuiteMutation, invoice.id],
  );

  const syncToNetSuite = useCallback(async () => {
    await runMutation(
      syncToNetSuiteMutation,
      { invoicesIds: [invoice.id] },
      {
        pending: 'Requesting sync to NetSuite...',
        success: 'Sync invoice initialized',
        error: 'Failed to initialize sync to NetSuite',
      },
      {
        onSuccess: async () => {
          await queryClient.invalidateQueries({
            queryKey: queryKeys.invoice(invoice.id),
          });
        },
      },
    );
  }, [syncToNetSuiteMutation, invoice.id]);

  const syncToNetSuiteSalesOrder = useCallback(() => {
    handleOpenSalesOrderConfirmDialog();
  }, [handleOpenSalesOrderConfirmDialog]);

  const confirmSyncToNetSuiteSalesOrder = useCallback(async () => {
    handleCloseSalesOrderConfirmDialog();
    await runMutation(
      syncToNetSuiteSalesOrderMutation,
      { invoicesIds: [invoice.id] },
      {
        pending: "Invoice sync started. You'll be notified when it's complete.",
        error: 'Failed to initialize sync to NetSuite as Sales Order',
      },
    );
  }, [syncToNetSuiteSalesOrderMutation, invoice.id, handleCloseSalesOrderConfirmDialog]);

  const syncToStripe = useCallback(async () => {
    await runMutation(
      syncInvoicesToStripeMutation,
      {
        invoiceIds: [invoice.id],
      },
      {
        pending: 'Syncing invoice to Stripe...',
        success: 'Invoice synced to Stripe',
        error: 'Failed to sync invoice to Stripe',
      },
      {
        onSuccess: async () => {
          await queryClient.invalidateQueries({
            queryKey: queryKeys.invoice(invoice.id),
          });
        },
      },
    );
  }, [syncInvoicesToStripeMutation, invoice.id]);

  const refund = useCallback(async () => {
    await runMutation(
      refundInvoiceMutation,
      {
        invoiceId: invoice.id,
      },
      {
        pending: 'Refunding invoice...',
        success: 'Invoice refunded',
        error: 'Failed to refund invoice',
      },
      {
        onSuccess: async ({ invoice: refundedInvoice }) => {
          await Promise.all([invalidateInvoicesQueries(queryClient), invalidatePaymentsQueries(queryClient)]);
          navigate(`/invoices/${refundedInvoice.id}/customer/${refundedInvoice.customerId}/dynamic`);
        },
      },
    );
  }, [refundInvoiceMutation, invoice.id, navigate, queryClient]);

  const approveSelectedInvoice = useCallback(async () => {
    await runMutation(
      updateInvoicesReviewStatusesMutation,
      {
        customerIds: [invoice.customer.id],
        invoicesByNewStatus: {
          [INVOICE_REVIEW_STATUSES.Approved]: [invoice.id],
        },
      },
      {
        pending: 'Approving invoice...',
        success: 'Invoice is approved',
        error: 'Failed to approve invoice',
      },
      {
        onSuccess: async () => {
          await Promise.all([
            queryClient.invalidateQueries({
              queryKey: queryKeys.invoice(invoice.id),
            }),
            queryClient.invalidateQueries({
              queryKey: queryKeys.dynamicInvoice(invoice.id),
            }),
            invalidateInvoicesQueries(queryClient),
          ]);
        },
      },
    );
  }, [queryClient, invoice, updateInvoicesReviewStatusesMutation]);

  const handleSyncToQuickBooks = useCallback(async () => {
    await runMutation(
      exportInvoiceToQuickBooksMutation,
      {
        invoiceIds: [invoice.id],
      },
      {
        pending: 'Exporting invoice to QuickBooks',
        success: 'Invoice exported to QuickBooks',
        error: 'Failed to export invoice to QuickBooks',
      },
      {
        onSuccess: async () => {
          await queryClient.invalidateQueries({
            queryKey: queryKeys.invoice(invoice.id),
          });
        },
      },
    );
  }, [exportInvoiceToQuickBooksMutation, invoice.id]);

  const grantCredit = useCallback(
    async ({
      invoiceId,
      contractId,
      customerId,
    }: {
      invoiceId: string;
      contractId: string | undefined;
      customerId: string;
    }) => {
      navigate(
        `/credit-notes/create?customerId=${customerId}&invoiceId=${invoiceId}&contractId=${contractId}`,
      );
    },
    [navigate],
  );

  const handleSyncToMorning = useCallback(async () => {
    await runMutation(
      exportInvoiceToMorningMutation,
      {
        invoiceIds: [invoice.id],
      },
      {
        pending: 'Exporting invoice to Morning',
        success: 'Invoice exported to Morning',
        error: 'Failed to export invoice to Morning',
      },
      {
        onSuccess: async () => {
          await queryClient.invalidateQueries({
            queryKey: queryKeys.invoice(invoice.id),
          });
        },
      },
    );
  }, [exportInvoiceToMorningMutation, invoice.id]);

  const handleSyncToMarketplace = useCallback(async () => {
    await runMutation(
      exportUsageToMarketplaceMutation,
      {
        invoicesIds: [invoice.id],
      },
      {
        pending: 'Exporting usage to Marketplace...',
        success: 'Usage export initiated',
        error: 'Failed to export usage to Marketplace',
      },
    );
  }, [exportUsageToMarketplaceMutation, invoice.id]);

  const downloadUsageEventsReport = useCallback(async () => {
    await runMutation(
      downloadUsageEventsReportMutation,
      { invoiceId: invoice.id },
      {
        pending: 'Downloading usage events report...',
        success: 'Usage events report downloaded',
        error: 'Failed to download usage events report',
      },
      {
        onSuccess: async ({ blob, isZip }) => {
          saveUsageEventsReport(blob, invoice.customer.name, invoice.name, isZip);
        },
      },
    );
  }, [downloadUsageEventsReportMutation, invoice]);

  const handleSyncToXero = useCallback(async () => {
    await runMutation(
      exportInvoiceToXeroMutation,
      {
        invoiceIds: [invoice.id],
      },
      {
        pending: 'Requesting sync to Xero...',
        success: 'Sync invoice initialized',
        error: 'Failed to initialize sync to Xero',
      },
    );
  }, [exportInvoiceToXeroMutation, invoice.id]);

  const handleSyncToPaddle = useCallback(async () => {
    await runMutation(
      exportInvoiceToPaddleMutation,
      {
        invoiceIds: [invoice.id],
      },
      {
        pending: 'Requesting sync to Paddle...',
        success: 'Sync invoice initialized',
        error: 'Failed to initialize sync to Paddle',
      },
    );
  }, [exportInvoiceToPaddleMutation, invoice.id]);

  return {
    downloadInvoicePdf,
    downloadUsageEventsReport,
    syncToNetSuite,
    syncToNetSuiteSalesOrder,
    confirmSyncToNetSuiteSalesOrder,
    isSalesOrderConfirmDialogOpen,
    handleCloseSalesOrderConfirmDialog,
    syncToStripe,
    refund,
    approveSelectedInvoice,
    grantCredit,
    isInvoiceApplicableForSync,
    handleSyncToQuickBooks,
    handleSyncToMorning,
    handleSyncToMarketplace,
    handleSyncToXero,
    handleSyncToPaddle,
  };
};
